/**
 * System-landscape diagram - server-side SVG renderer.
 *
 * Produces a deterministic SVG string (no DOM/React) that visually resembles the
 * `SystemLandscapeGrid` web diagram, for embedding the "Future State Systems Landscape"
 * figure in a generated SOW. Layout comes from `resolveSystemLandscapeLayout` in
 * `@protoss/shared`; this file owns only the drawing + the light-theme palette.
 */
import { resolveSystemLandscapeLayout, } from '@protoss/shared';
// Light-theme palette - approximates the CSS `color-mix` tints used by the web cards.
const TONE = {
    neutral: { bg: '#ffffff', border: '#e5e7eb', eyebrow: '#9ca3af' },
    success: { bg: '#eaf7ee', border: '#a7dab5', eyebrow: '#15803d' },
    warning: { bg: '#fef6e7', border: '#f6d68a', eyebrow: '#b45309' },
    danger: { bg: '#fdecec', border: '#f4b4b4', eyebrow: '#dc2626' },
};
const PRIMARY_COLOR = '#111827';
const SECONDARY_COLOR = '#6b7280';
const HEADER_COLOR = '#6b7280';
const EDGE_COLOR = '#cbd2da';
const CARD_RADIUS = 12;
const PAD = 12;
const ARROW_ID = 'sl-arrow';
function escapeXml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** Deterministic glyph-width estimate (no DOM measurement). */
function textWidth(text, fontSize) {
    return text.length * fontSize * 0.56;
}
/** Greedy word-wrap to `maxWidth`, capped at `maxLines`; the last line is ellipsised on overflow. */
function wrapText(text, maxWidth, fontSize, maxLines) {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return [];
    const lines = [];
    let current = '';
    for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (textWidth(candidate, fontSize) <= maxWidth || !current) {
            current = candidate;
        }
        else {
            lines.push(current);
            current = word;
            if (lines.length === maxLines)
                break;
        }
    }
    if (lines.length < maxLines && current)
        lines.push(current);
    // If content remains beyond maxLines, ellipsise the final line.
    const consumed = lines.join(' ').split(/\s+/).filter(Boolean).length;
    if (consumed < words.length && lines.length > 0) {
        let last = lines[maxLines - 1] ?? lines[lines.length - 1] ?? '';
        while (last && textWidth(`${last}…`, fontSize) > maxWidth) {
            last = last.slice(0, -1);
        }
        lines[lines.length - 1] = `${last.trimEnd()}…`;
    }
    return lines.slice(0, maxLines);
}
function buildCardLines(label, currentTool, postPlan, tone, contentWidth, sizes) {
    const lines = [
        {
            text: label,
            size: sizes.eyebrow,
            fill: TONE[tone].eyebrow,
            weight: 600,
            letterSpacing: 0.4,
            upper: true,
        },
    ];
    if (currentTool && currentTool.trim()) {
        for (const ln of wrapText(currentTool.trim(), contentWidth, sizes.primary, 2)) {
            lines.push({ text: ln, size: sizes.primary, fill: PRIMARY_COLOR, weight: 600 });
        }
    }
    if (postPlan && postPlan.trim()) {
        // postPlan is rendered as-is (no "→" prefix) - graph direction is shown by the edges.
        for (const ln of wrapText(postPlan.trim(), contentWidth, sizes.secondary, 2)) {
            lines.push({ text: ln, size: sizes.secondary, fill: SECONDARY_COLOR, weight: 400 });
        }
    }
    return lines;
}
function renderTextLines(centerX, startY, lines) {
    let y = startY;
    const out = [];
    for (const line of lines) {
        y += line.size; // advance to baseline
        const text = escapeXml(line.upper ? line.text.toUpperCase() : line.text);
        const ls = line.letterSpacing ? ` letter-spacing="${line.letterSpacing}"` : '';
        out.push(`<text x="${centerX.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" ` +
            `font-family="Liberation Sans, sans-serif" font-size="${line.size}" ` +
            `font-weight="${line.weight}" fill="${line.fill}"${ls}>${text}</text>`);
        y += line.size * 0.42; // inter-line gap
    }
    return out.join('');
}
function linesHeight(lines) {
    return lines.reduce((sum, l) => sum + l.size * 1.42, 0);
}
function roundRect(x, y, w, h, r, fill, stroke, strokeWidth = 1) {
    return (`<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" ` +
        `rx="${r}" ry="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`);
}
const LOGO_H = 30;
const BADGE_R = 15;
/**
 * Logo card (Maxio flow diagram): category eyebrow, then the logo (or an initials badge), then the
 * product name, then an optional status line and add-on chips. Used only when the layout sets
 * `logo` or `initials`, so every other diagram keeps the classic text card.
 */
function renderLogoNode(node) {
    const palette = TONE[node.tone];
    const border = node.accent ?? palette.border;
    const centerX = node.x + node.width / 2;
    const out = [
        roundRect(node.x, node.y, node.width, node.height, CARD_RADIUS, palette.bg, border, node.accent ? 2 : 1),
    ];
    const contentWidth = node.width - PAD * 2;
    const lines = [];
    if (node.currentTool && node.currentTool.trim()) {
        for (const ln of wrapText(node.currentTool.trim(), contentWidth, 13.5, 2)) {
            lines.push({ text: ln, size: 13.5, fill: PRIMARY_COLOR, weight: 600 });
        }
    }
    if (node.postPlan && node.postPlan.trim()) {
        lines.push({
            text: node.postPlan.trim(),
            size: 9.5,
            fill: node.tone === 'danger' ? palette.eyebrow : SECONDARY_COLOR,
            weight: 500,
        });
    }
    const chips = node.chips ?? [];
    // The whole block (eyebrow, logo, name, status, chips) sits in the vertical middle of the card.
    // Without chips the last line's trailing gap is not part of the visible block.
    const textHeight = lines.length && !chips.length
        ? linesHeight(lines) - lines.at(-1).size * 0.42
        : linesHeight(lines);
    const chipsHeight = chips.length ? 4 + Math.ceil(chips.length / 2) * 26 - 6 : 0;
    const blockHeight = 18 + LOGO_H + 4 + textHeight + chipsHeight;
    let y = node.y + Math.max(PAD, (node.height - blockHeight) / 2);
    out.push(renderTextLines(centerX, y - 2, [
        {
            text: node.label,
            size: 9.5,
            fill: palette.eyebrow,
            weight: 600,
            letterSpacing: 0.4,
            upper: true,
        },
    ]));
    y += 18;
    if (node.logo) {
        const w = Math.min(contentWidth, 110);
        out.push(`<image x="${(centerX - w / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${LOGO_H}" ` +
            `preserveAspectRatio="xMidYMid meet" href="${escapeXml(node.logo)}"/>`);
    }
    else if (node.initials) {
        const cy = y + LOGO_H / 2;
        out.push(`<circle cx="${centerX.toFixed(1)}" cy="${cy.toFixed(1)}" r="${BADGE_R}" fill="${node.accent ?? '#334155'}"/>` +
            `<text x="${centerX.toFixed(1)}" y="${(cy + 4).toFixed(1)}" text-anchor="middle" ` +
            `font-family="Liberation Sans, sans-serif" font-size="11" font-weight="700" fill="#ffffff">` +
            `${escapeXml(node.initials)}</text>`);
    }
    y += LOGO_H + 4;
    out.push(renderTextLines(centerX, y, lines));
    y += linesHeight(lines) + 4;
    // Add-on chips, two per row.
    const chipW = (contentWidth - 8) / 2;
    chips.forEach((chip, i) => {
        const cx = node.x + PAD + (i % 2) * (chipW + 8);
        const cy = y + Math.floor(i / 2) * 26;
        out.push(roundRect(cx, cy, chipW, 20, 10, '#eef2ff', '#c7d2fe'));
        out.push(`<text x="${(cx + chipW / 2).toFixed(1)}" y="${(cy + 13.5).toFixed(1)}" text-anchor="middle" ` +
            `font-family="Liberation Sans, sans-serif" font-size="9.5" font-weight="600" fill="#3730a3">` +
            `${escapeXml(chip)}</text>`);
    });
    return out.join('');
}
function renderNode(node) {
    if (node.logo || node.initials)
        return renderLogoNode(node);
    const palette = TONE[node.tone];
    const centerX = node.x + node.width / 2;
    const out = [
        roundRect(node.x, node.y, node.width, node.height, CARD_RADIUS, palette.bg, palette.border),
    ];
    const contentWidth = node.width - PAD * 2;
    const compact = node.variant === 'compact';
    const sizes = compact
        ? { eyebrow: 9.5, primary: 13, secondary: 9.5 }
        : { eyebrow: 10.5, primary: 15, secondary: 10.5 };
    if (node.kind === 'erp' && node.embeddedChild) {
        // ERP own text top-aligned, embedded child box in the lower portion.
        const ownLines = buildCardLines(node.label, node.currentTool, node.postPlan, node.tone, contentWidth, sizes);
        out.push(renderTextLines(centerX, node.y + PAD, ownLines));
        const child = node.embeddedChild;
        const childTone = TONE[child.tone];
        const boxH = 92;
        const boxX = node.x + PAD;
        const boxY = node.y + node.height - boxH - PAD;
        const boxW = node.width - PAD * 2;
        out.push(roundRect(boxX, boxY, boxW, boxH, 8, childTone.bg, childTone.border));
        const childLines = buildCardLines(child.label, child.currentTool, child.postPlan, child.tone, boxW - 16, { eyebrow: 9.5, primary: 12.5, secondary: 9.5 });
        const childStart = boxY + (boxH - linesHeight(childLines)) / 2;
        out.push(renderTextLines(boxX + boxW / 2, childStart, childLines));
        return out.join('');
    }
    const lines = buildCardLines(node.label, node.currentTool, node.postPlan, node.tone, contentWidth, sizes);
    const startY = node.y + Math.max(PAD, (node.height - linesHeight(lines)) / 2);
    out.push(renderTextLines(centerX, startY, lines));
    return out.join('');
}
function handlePoint(node, handle) {
    switch (handle) {
        case 'right':
            return { x: node.x + node.width, y: node.y + node.height / 2 };
        case 'left':
            return { x: node.x, y: node.y + node.height / 2 };
        case 'top':
            return { x: node.x + node.width / 2, y: node.y };
        case 'bottom':
            return { x: node.x + node.width / 2, y: node.y + node.height };
        default:
            return { x: node.x + node.width / 2, y: node.y + node.height / 2 };
    }
}
function edgePath(src, dst, srcHandle, dstHandle, via) {
    const horizontal = (srcHandle === 'right' || srcHandle === 'left') &&
        (dstHandle === 'left' || dstHandle === 'right');
    if (horizontal) {
        const mid = via ?? (src.x + dst.x) / 2;
        return `M ${src.x} ${src.y} L ${mid} ${src.y} L ${mid} ${dst.y} L ${dst.x} ${dst.y}`;
    }
    if (src.x === dst.x)
        return `M ${src.x} ${src.y} L ${dst.x} ${dst.y}`;
    const mid = via ?? (src.y + dst.y) / 2;
    return `M ${src.x} ${src.y} L ${src.x} ${mid} L ${dst.x} ${mid} L ${dst.x} ${dst.y}`;
}
function polylinePath(points) {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
}
/**
 * Render an already-resolved system-landscape layout to an SVG string. Returns '' when the
 * layout has no nodes, so callers can omit the figure/section. Used for both the grid
 * topology (`renderSystemLandscapeSvg`) and the hub-and-spoke tech-stack layout
 * (`buildTechStackHubSpokeLayout`); the renderer is layout-agnostic.
 */
export function renderResolvedSystemLandscapeSvg(layout, options = {}) {
    if (layout.nodes.length === 0)
        return '';
    const nodeById = new Map(layout.nodes.map((n) => [n.id, n]));
    const W = layout.canvasWidth;
    const H = layout.canvasHeight;
    const background = options.background ?? '#f3f4f6';
    const parts = [];
    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`);
    parts.push(`<defs><marker id="${ARROW_ID}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">` +
        `<path d="M 0 1 L 9 5 L 0 9 z" fill="${EDGE_COLOR}"/></marker></defs>`);
    parts.push(`<rect x="0" y="0" width="${W}" height="${H}" rx="14" fill="${background}"/>`);
    // Column headers.
    for (const col of layout.columns) {
        const cx = col.x + col.width / 2;
        const y = layout.headerTop + layout.headerHeight - 4;
        parts.push(`<text x="${cx.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="middle" ` +
            `font-family="Liberation Sans, sans-serif" font-size="11" font-weight="700" ` +
            `letter-spacing="0.8" fill="${HEADER_COLOR}">${escapeXml(col.label.toUpperCase())}</text>`);
    }
    // Edges first, so cards sit on top.
    for (const edge of layout.edges) {
        const src = nodeById.get(edge.source);
        const dst = nodeById.get(edge.target);
        if (!src || !dst)
            continue;
        const srcPt = edge.sourcePoint ?? handlePoint(src, edge.sourceHandle);
        const dstPt = edge.targetPoint ?? handlePoint(dst, edge.targetHandle);
        const paths = [];
        if (edge.waypoints && edge.waypoints.length > 0) {
            const route = [srcPt, ...edge.waypoints, dstPt];
            paths.push(polylinePath(route));
            if (edge.bidirectional)
                paths.push(polylinePath([...route].reverse()));
        }
        else {
            paths.push(edgePath(srcPt, dstPt, edge.sourceHandle, edge.targetHandle, edge.via));
            if (edge.bidirectional) {
                paths.push(edgePath(dstPt, srcPt, edge.targetHandle, edge.sourceHandle, edge.via));
            }
        }
        for (const d of paths) {
            parts.push(`<path d="${d}" fill="none" stroke="${EDGE_COLOR}" stroke-width="1.5" marker-end="url(#${ARROW_ID})"/>`);
        }
    }
    // Cards.
    for (const node of layout.nodes) {
        parts.push(renderNode(node));
    }
    parts.push('</svg>');
    return parts.join('');
}
/**
 * Render the grid system-landscape diagram to an SVG string. Returns '' when there are no
 * active nodes (no tech-stack selections), so callers can omit the figure/section.
 */
export function renderSystemLandscapeSvg(selections, config, options = {}) {
    return renderResolvedSystemLandscapeSvg(resolveSystemLandscapeLayout({ selections, config }), options);
}
//# sourceMappingURL=system-landscape-svg.js.map